import { useEffect, useState } from "react";
import _get from "lodash-es/get";

import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";

const cache = new Map();
const inflight = new Map();

const loadOnce = async (cacheKey, fetcher) => {
  if (cache.has(cacheKey)) return cache.get(cacheKey);
  if (inflight.has(cacheKey)) return inflight.get(cacheKey);

  const p = (async () => {
    try {
      const options = await fetcher();
      cache.set(cacheKey, options);
      return options;
    } finally {
      inflight.delete(cacheKey);
    }
  })();
  inflight.set(cacheKey, p);
  return p;
};

const useRemoteOptions = (cacheKey, fetcher) => {
  const [state, setState] = useState(() =>
    cache.has(cacheKey)
      ? { options: cache.get(cacheKey), loading: false, error: null }
      : { options: null, loading: true, error: null }
  );

  useEffect(() => {
    if (cache.has(cacheKey)) return undefined;
    let alive = true;
    loadOnce(cacheKey, fetcher)
      .then((options) => alive && setState({ options, loading: false, error: null }))
      .catch((err) =>
        alive &&
        setState({
          options: [],
          loading: false,
          error: _get(err, ["response", "data", "error", "message"], "Failed to load options"),
        })
      );
    return () => {
      alive = false;
    };
    
  }, [cacheKey]);

  return state;
};

// stateValue is included so city selection can auto-fill shipping_state via onOptionSelect.
export const useCityOptions = () =>
  useRemoteOptions("cities", async () => {
    const res = await api.get(API_HOST_WMS, APIendpoints.getAllCities);
    const raw = _get(res, ["data", "response", "cities"], []);
    return raw
      .map((c) => {
        const name = typeof c === "string" ? c : c?.city_name || c?.name;
        if (!name) return null;
        const stateValue = typeof c === "object" ? (c?.state_name || null) : null;
        return { label: name, value: name, stateValue };
      })
      .filter(Boolean);
  });

export const useCategoryOptions = () =>
  useRemoteOptions("categories", async () => {
    const res = await api.get(
      API_HOST_WMS,
      `${APIendpoints.sellerCategories}?get_parent_categories_only=true`
    );
    const raw = _get(res, ["data", "response"], []);
    const list = Array.isArray(raw) ? raw : raw?.categories || [];
    return list
      .map((c) => {
        if (typeof c === "string") return { label: c, value: c };
        const label = c?.name || c?.title || c?.category_name;
        return label ? { label, value: label } : null;
      })
      .filter(Boolean);
  });
